import {
  Component,
  ElementRef,
  Inject,
  Injectable,
  ViewChild,
  inject,
} from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import { UiButtonDirective } from '@template-ui/directives';
import { EzDialogComponent } from '@template-ui/components/ez-dialog/ez-dialog.component';
import type { EzDialogData } from '@template-ui/types/ez-dialog.types';
import type { FormIssues } from '@template-ui/helpers/generated-form.helpers';
import {
  reasonCaptureIssues,
  type WorkflowReasonCapturePresentation,
  type WorkflowReasonCaptureValue,
} from '@app/shared/workflow-commands/workflow-reason-capture';
import { WorkflowReasonCaptureComponent } from './workflow-reason-capture.component';

export interface WorkflowReasonCaptureDialogRequest {
  heading: string;
  presentation: WorkflowReasonCapturePresentation;
  value: WorkflowReasonCaptureValue;
  /** Recheck the owning visit, action and catalog before accepting any local input. */
  isCurrent: () => boolean;
  valueChange: (value: WorkflowReasonCaptureValue) => void;
}
interface DialogData extends EzDialogData, WorkflowReasonCaptureDialogRequest {}
export interface WorkflowReasonCaptureDialogHandle {
  closed: Promise<WorkflowReasonCaptureValue | null>;
  close: (clear?: boolean) => void;
}

@Component({
  selector: 'app-workflow-reason-capture-dialog',
  standalone: true,
  imports: [
    EzDialogComponent,
    WorkflowReasonCaptureComponent,
    UiButtonDirective,
  ],
  template: `<ui-dialog [contentTemplate]="body" />
    <ng-template #body>
      <form #form (submit)="$event.preventDefault(); confirm()" novalidate>
        <app-workflow-reason-capture
          [presentation]="data.presentation"
          [value]="value"
          [issues]="issues"
          (valueChange)="changed($event)"
        />
        <div class="actions">
          <button uiButton type="button" (click)="cancel()">
            Keep draft and close
          </button>
          <button
            uiButton
            type="submit"
            [disabled]="!!data.presentation.unavailable"
          >
            Use reason
          </button>
        </div>
      </form>
    </ng-template>`,
  styles: [
    `
      form {
        display: grid;
        gap: 1rem;
      }
      .actions {
        display: flex;
        flex-wrap: wrap;
        justify-content: flex-end;
        gap: 0.5rem;
      }
    `,
  ],
})
export class WorkflowReasonCaptureDialogComponent {
  @ViewChild('form') form?: ElementRef<HTMLFormElement>;
  value: WorkflowReasonCaptureValue;
  issues: FormIssues = {};
  constructor(
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    private readonly dialogRef: MatDialogRef<WorkflowReasonCaptureDialogComponent>,
  ) {
    this.value = { ...data.value };
  }
  changed(value: WorkflowReasonCaptureValue): void {
    if (!this.data.isCurrent()) {
      this.clear();
      this.dialogRef.close(null);
      return;
    }
    this.value = { ...value };
    this.issues = {};
    this.data.valueChange({ ...value });
  }
  confirm(): void {
    if (!this.data.isCurrent()) {
      this.clear();
      this.dialogRef.close(null);
      return;
    }
    this.issues = reasonCaptureIssues(this.data.presentation, this.value);
    if (Object.keys(this.issues).length) {
      const field = this.data.presentation.contract.codeField;
      queueMicrotask(() =>
        this.form?.nativeElement
          .querySelector<HTMLElement>(
            field && this.issues[field] ? 'select' : 'textarea',
          )
          ?.focus(),
      );
      return;
    }
    this.data.valueChange({ ...this.value });
    this.dialogRef.close({ ...this.value });
  }
  cancel(): void {
    this.dialogRef.close(null);
  }
  clear(): void {
    this.value = { code: '', text: '' };
    this.issues = {};
  }
}

@Injectable({ providedIn: 'root' })
export class WorkflowReasonCaptureDialogService {
  private readonly dialog = inject(MatDialog);
  open(
    request: WorkflowReasonCaptureDialogRequest,
  ): WorkflowReasonCaptureDialogHandle {
    const ref = this.dialog.open(WorkflowReasonCaptureDialogComponent, {
      width: '34rem',
      maxWidth: 'calc(100vw - 2rem)',
      autoFocus: 'first-tabbable',
      restoreFocus: true,
      disableClose: false,
      role: 'dialog',
      ariaLabel: request.heading,
      data: {
        ...request,
        buttons: [],
        useForm: false,
        headerStyle: 'info',
      } satisfies DialogData,
    });
    return {
      closed: firstValueFrom(ref.afterClosed()).then((value) => value ?? null),
      close: (clear = false) => {
        if (clear) ref.componentInstance.clear();
        ref.close(null);
      },
    };
  }
}
